import { useState, useMemo, useCallback, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Calendar as BigCalendar, View, dateFnsLocalizer, SlotInfo } from "react-big-calendar";
import { format, parse, startOfWeek, getDay, addMonths, subMonths, isSameDay, startOfDay } from "date-fns";
import { enUS } from "date-fns/locale";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "@/styles/calendar.css";

import { PageBackground } from "@/components/ui/page-background";
import { AppHeader } from "@/components/app-header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Calendar, ChevronLeft, ChevronRight, ArrowLeft,
  Music, MapPin, PoundSterling, Clock, Eye, CalendarDays, Layers
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import type { Booking, ExternalEvent, ActWithParticipants } from "@shared/schema";

// Setup date-fns localizer for react-big-calendar
const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Define calendar event type
interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource: {
    type: 'booking' | 'event' | 'multiple';
    data: any;
    status: string;
    fee?: number;
    venue?: string;
    act?: string;
  };
}

export default function BookingsCalendar() {
  const { orgId } = useParams();
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [view, setView] = useState<View>("month");
  const [date, setDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [showDayEventsModal, setShowDayEventsModal] = useState(false);
  const [selectedDayEvents, setSelectedDayEvents] = useState<CalendarEvent[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  
  const organisationId = parseInt(orgId || "0");

  // Check permissions
  useEffect(() => {
    if (!orgId) {
      setLocation("/");
      return;
    }

    const hasAccess = user?.role === 'god' || 
      user?.organisationUsers?.some(ou => 
        ou.organisation.id === organisationId
      );

    if (!hasAccess) {
      setLocation("/");
    }
  }, [orgId, user, setLocation, organisationId]);

  // Fetch bookings
  const { data: bookings = [] } = useQuery<any[]>({
    queryKey: [`/api/bookings?organisationId=${organisationId}`],
    queryFn: async () => {
      const response = await fetch(`/api/bookings?organisationId=${organisationId}`);
      if (!response.ok) throw new Error("Failed to fetch bookings");
      return response.json();
    },
    enabled: !!organisationId,
  });

  // Fetch external events
  const { data: externalEvents = [] } = useQuery<any[]>({
    queryKey: [`/api/external-events?organisationId=${organisationId}`],
    queryFn: async () => {
      const response = await fetch(`/api/external-events?organisationId=${organisationId}`);
      if (!response.ok) throw new Error("Failed to fetch events");
      return response.json();
    },
    enabled: !!organisationId,
  });

  // Transform data into calendar events - raw events before grouping
  const allCalendarEvents = useMemo<CalendarEvent[]>(() => {
    const events: CalendarEvent[] = [];

    // Add all bookings
    bookings.forEach((booking) => {
        if (booking.performanceTime || booking.externalEvent?.date) {
          const eventDate = booking.performanceTime 
            ? new Date(booking.performanceTime)
            : new Date(booking.externalEvent.date);
          
          const actName = booking.act?.name || 
            booking.act?.participants?.[0]?.participant?.name || 
            'Unknown Act';
          
          events.push({
            id: `booking-${booking.id}`,
            title: `📅 ${actName}`,
            start: eventDate,
            end: new Date(eventDate.getTime() + (booking.durationMinutes || 60) * 60000),
            resource: {
              type: 'booking',
              data: booking,
              status: booking.status,
              fee: booking.fee,
              venue: booking.externalEvent?.venue?.name || booking.externalEvent?.name,
              act: actName,
            },
        });
      }
    });

    // Add all external events without bookings
    externalEvents.forEach((event) => {
        // Check if this event already has a booking
        const hasBooking = bookings.some(b => b.externalEventId === event.id);
        
        if (!hasBooking && event.date) {
          events.push({
            id: `event-${event.id}`,
            title: `🎭 ${event.name}`,
            start: new Date(event.date),
            end: new Date(new Date(event.date).getTime() + 3 * 60 * 60000), // Default 3 hours
            resource: {
              type: 'event',
              data: event,
              status: event.status,
              venue: event.venue?.name,
            },
        });
      }
    });

    return events;
  }, [bookings, externalEvents]);

  // Group events by day for month view
  const calendarEvents = useMemo<CalendarEvent[]>(() => {
    if (view !== 'month') {
      // For week, day, and agenda views, show all events
      return allCalendarEvents;
    }

    // Group events by day for month view
    const eventsByDay = new Map<string, CalendarEvent[]>();
    
    allCalendarEvents.forEach(event => {
      const dayKey = format(startOfDay(event.start), 'yyyy-MM-dd');
      if (!eventsByDay.has(dayKey)) {
        eventsByDay.set(dayKey, []);
      }
      eventsByDay.get(dayKey)!.push(event);
    });

    // Create display events
    const displayEvents: CalendarEvent[] = [];
    
    eventsByDay.forEach((dayEvents, dayKey) => {
      if (dayEvents.length === 1) {
        // Single event - show as normal
        displayEvents.push(dayEvents[0]);
      } else {
        // Multiple events - create a special grouped event
        const date = new Date(dayKey);
        // Use the same start and end time (just a few hours) so it doesn't span multiple days
        displayEvents.push({
          id: `multi-${dayKey}`,
          title: `📚 ${dayEvents.length} Events`,
          start: date,
          end: new Date(date.getTime() + 3 * 60 * 60000), // Just 3 hours duration for display
          resource: {
            type: 'multiple',
            data: dayEvents,
            status: 'multiple',
            venue: `${dayEvents.length} locations`,
            act: `${dayEvents.length} events`,
          },
        });
      }
    });

    return displayEvents;
  }, [allCalendarEvents, view]);

  // Event style getter
  const eventStyleGetter = useCallback((event: CalendarEvent) => {
    let backgroundColor = '#3b82f6'; // Default blue
    let borderColor = '#2563eb';
    
    if (event.resource.type === 'multiple') {
      backgroundColor = '#6366f1'; // Indigo for multiple events
      borderColor = '#4f46e5';
    } else if (event.resource.type === 'booking') {
      switch (event.resource.status) {
        case 'confirmed':
          backgroundColor = '#10b981';
          borderColor = '#059669';
          break;
        case 'pending':
          backgroundColor = '#f59e0b';
          borderColor = '#d97706';
          break;
        case 'cancelled':
          backgroundColor = '#ef4444';
          borderColor = '#dc2626';
          break;
        case 'completed':
          backgroundColor = '#6b7280';
          borderColor = '#4b5563';
          break;
      }
    } else {
      // External events
      switch (event.resource.status) {
        case 'confirmed':
          backgroundColor = '#8b5cf6';
          borderColor = '#7c3aed';
          break;
        case 'tentative':
          backgroundColor = '#f97316';
          borderColor = '#ea580c';
          break;
        case 'cancelled':
          backgroundColor = '#ef4444';
          borderColor = '#dc2626';
          break;
      }
    }

    return {
      style: {
        backgroundColor: backgroundColor + '99', // Add transparency
        borderLeft: `4px solid ${borderColor}`,
        color: 'white',
        borderRadius: '8px',
        padding: '2px 6px',
        fontSize: '0.875rem',
        fontWeight: '500',
        backdropFilter: 'blur(8px)',
      },
    };
  }, []);

  // Handle event selection
  const handleSelectEvent = useCallback((event: CalendarEvent) => {
    if (event.resource.type === 'multiple') {
      // Show modal with all events for this day
      const dayEvents = event.resource.data as CalendarEvent[];
      setSelectedDayEvents(dayEvents);
      setSelectedDate(event.start);
      setShowDayEventsModal(true);
    } else if (event.resource.type === 'booking') {
      setLocation(`/organisations/${orgId}/bookings/${event.resource.data.id}`);
    } else {
      setLocation(`/organisations/${orgId}/events/${event.resource.data.id}/summary`);
    }
  }, [orgId, setLocation]);

  // Handle event selection from modal
  const handleModalEventClick = useCallback((event: CalendarEvent) => {
    setShowDayEventsModal(false);
    if (event.resource.type === 'booking') {
      setLocation(`/organisations/${orgId}/bookings/${event.resource.data.id}`);
    } else {
      setLocation(`/organisations/${orgId}/events/${event.resource.data.id}/summary`);
    }
  }, [orgId, setLocation]);

  // Custom toolbar component
  const CustomToolbar = useCallback((toolbar: any) => {
    const goToBack = () => {
      toolbar.onNavigate('PREV');
    };

    const goToNext = () => {
      toolbar.onNavigate('NEXT');
    };

    const goToToday = () => {
      toolbar.onNavigate('TODAY');
    };

    const label = () => {
      const date = toolbar.date;
      return (
        <span className="text-lg font-semibold text-slate-900">
          {format(date, toolbar.view === 'month' ? 'MMMM yyyy' : 'dd MMMM yyyy')}
        </span>
      );
    };

    return (
      <div className="flex flex-col gap-3 mb-4 p-4 bg-white/30 backdrop-blur-md rounded-xl border border-white/20">
        {/* Date navigation - always on same row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 sm:gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goToBack}
              className="bg-white/40 backdrop-blur-sm border-white/30 hover:bg-white/50 px-2 sm:px-3"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={goToNext}
              className="bg-white/40 backdrop-blur-sm border-white/30 hover:bg-white/50 px-2 sm:px-3"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="text-sm sm:text-base font-semibold text-slate-900">{label()}</div>
        </div>

        {/* View controls - separate row */}
        <div className="flex justify-center sm:justify-end">
          <div className="grid grid-cols-4 sm:flex gap-1 bg-white/40 backdrop-blur-sm rounded-lg p-1 border border-white/30">
            <Button
              variant={toolbar.view === 'month' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => toolbar.onView('month')}
              className={`text-xs sm:text-sm px-2 sm:px-3 ${toolbar.view === 'month' ? 'bg-blue-600 text-white' : ''}`}
            >
              Month
            </Button>
            <Button
              variant={toolbar.view === 'week' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => toolbar.onView('week')}
              className={`text-xs sm:text-sm px-2 sm:px-3 ${toolbar.view === 'week' ? 'bg-blue-600 text-white' : ''}`}
            >
              Week
            </Button>
            <Button
              variant={toolbar.view === 'day' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => toolbar.onView('day')}
              className={`text-xs sm:text-sm px-2 sm:px-3 ${toolbar.view === 'day' ? 'bg-blue-600 text-white' : ''}`}
            >
              Day
            </Button>
            <Button
              variant={toolbar.view === 'agenda' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => toolbar.onView('agenda')}
              className={`text-xs sm:text-sm px-2 sm:px-3 ${toolbar.view === 'agenda' ? 'bg-blue-600 text-white' : ''}`}
            >
              List
            </Button>
          </div>
        </div>
      </div>
    );
  }, []);

  // Custom event component
  const CustomEvent = useCallback(({ event }: { event: CalendarEvent }) => {
    return (
      <div className="cursor-pointer p-1">
        <div className="font-medium text-xs truncate">{event.title}</div>
        {event.resource.venue && (
          <div className="text-xs opacity-90 truncate">📍 {event.resource.venue}</div>
        )}
        {event.resource.fee && (
          <div className="text-xs opacity-90">£{(event.resource.fee / 100).toFixed(0)}</div>
        )}
      </div>
    );
  }, []);

  return (
    <PageBackground>
      <AppHeader />
      <main className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => setLocation(`/organisations/${orgId}/acts`)}
              className="bg-white/40 backdrop-blur-sm border-white/30 hover:bg-white/50"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Acts
            </Button>
            <h1 className="text-3xl font-bold text-slate-900 drop-shadow-sm">
              Bookings Calendar
            </h1>
          </div>
        </div>

        {/* Calendar Card */}
        <Card 
          className="bg-white/20 backdrop-blur-xl rounded-2xl border border-white/30 p-6"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
          }}
        >
          {/* Legend */}
          <div className="mb-4 p-3 bg-white/20 backdrop-blur-sm rounded-lg border border-white/20">
            <div className="text-sm font-medium text-slate-700 mb-2">Legend:</div>
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded flex-shrink-0"></div>
                <span className="text-xs sm:text-sm text-slate-600">Confirmed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-amber-500 rounded flex-shrink-0"></div>
                <span className="text-xs sm:text-sm text-slate-600">Pending</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-purple-500 rounded flex-shrink-0"></div>
                <span className="text-xs sm:text-sm text-slate-600">Event</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded flex-shrink-0"></div>
                <span className="text-xs sm:text-sm text-slate-600">Cancelled</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-gray-500 rounded flex-shrink-0"></div>
                <span className="text-xs sm:text-sm text-slate-600">Completed</span>
              </div>
            </div>
          </div>

          {/* Calendar */}
          <div 
            className="bg-white/50 backdrop-blur-sm rounded-xl p-2 sm:p-4 border border-white/30"
            style={{ height: "60vh", minHeight: "400px" }}
          >
            <BigCalendar
              localizer={localizer}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              view={view}
              onView={setView}
              date={date}
              onNavigate={setDate}
              eventPropGetter={eventStyleGetter}
              onSelectEvent={handleSelectEvent}
              components={{
                toolbar: CustomToolbar,
                event: CustomEvent,
              }}
              popup
              showMultiDayTimes
              style={{ 
                height: "100%",
                fontFamily: "inherit",
              }}
            />
          </div>

          {/* Stats Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-white/30 backdrop-blur-sm rounded-lg p-3 border border-white/20">
              <div className="text-sm text-slate-600 mb-1">Total Bookings</div>
              <div className="text-2xl font-bold text-slate-900">
                {bookings.filter(b => b.status !== 'cancelled').length}
              </div>
            </div>
            <div className="bg-white/30 backdrop-blur-sm rounded-lg p-3 border border-white/20">
              <div className="text-sm text-slate-600 mb-1">Confirmed</div>
              <div className="text-2xl font-bold text-green-600">
                {bookings.filter(b => b.status === 'confirmed').length}
              </div>
            </div>
            <div className="bg-white/30 backdrop-blur-sm rounded-lg p-3 border border-white/20">
              <div className="text-sm text-slate-600 mb-1">Pending</div>
              <div className="text-2xl font-bold text-amber-600">
                {bookings.filter(b => b.status === 'pending').length}
              </div>
            </div>
            <div className="bg-white/30 backdrop-blur-sm rounded-lg p-3 border border-white/20">
              <div className="text-sm text-slate-600 mb-1">Total Revenue</div>
              <div className="text-2xl font-bold text-slate-900">
                £{(bookings
                  .filter(b => b.status === 'confirmed' || b.status === 'completed')
                  .reduce((sum, b) => sum + (b.fee || 0), 0) / 100
                ).toFixed(0)}
              </div>
            </div>
          </div>
        </Card>

        {/* Multiple Events Modal */}
        <Dialog open={showDayEventsModal} onOpenChange={setShowDayEventsModal}>
          <DialogContent className="max-w-2xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-blue-600" />
                Events on {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}
              </DialogTitle>
              <DialogDescription>
                {selectedDayEvents.length} events scheduled for this day
              </DialogDescription>
            </DialogHeader>
            
            <ScrollArea className="h-[50vh] pr-4">
              <div className="space-y-3">
                {selectedDayEvents.map((event) => {
                  const isBooking = event.resource.type === 'booking';
                  const statusColor = getStatusBadgeColor(event.resource.status);
                  
                  return (
                    <Card
                      key={event.id}
                      className="bg-white/20 backdrop-blur-xl border-white/30 hover:bg-white/30 transition-all duration-300 cursor-pointer group"
                      style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                      }}
                      onClick={() => handleModalEventClick(event)}
                    >
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-lg">{isBooking ? '📅' : '🎭'}</span>
                              <h3 className="font-semibold text-slate-900">
                                {event.title.replace(/^[📅🎭]\s/, '')}
                              </h3>
                            </div>
                            
                            {event.resource.venue && (
                              <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                                <MapPin className="h-3 w-3" />
                                <span>{event.resource.venue}</span>
                              </div>
                            )}
                            
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                              <Clock className="h-3 w-3" />
                              <span>{format(event.start, 'h:mm a')}</span>
                              {event.resource.type === 'booking' && event.resource.data.durationMinutes && (
                                <span className="text-slate-500">
                                  ({event.resource.data.durationMinutes} min)
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex flex-col items-end gap-2">
                            <Badge className={statusColor}>
                              {event.resource.status}
                            </Badge>
                            
                            {event.resource.fee && (
                              <div className="text-sm font-medium text-slate-700">
                                £{(event.resource.fee / 100).toFixed(2)}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="pt-2 border-t border-white/20 mt-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="w-full bg-white/20 hover:bg-white/30 group-hover:bg-white/30"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleModalEventClick(event);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </Button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </main>
    </PageBackground>
  );
}

// Helper function to get status badge color
function getStatusBadgeColor(status: string): string {
  switch (status) {
    case 'confirmed':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'cancelled':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'completed':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    case 'tentative':
      return 'bg-orange-100 text-orange-800 border-orange-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
}